"use client";
import { useState } from "react";
import { usePathname } from "next/navigation";
import validator from "@/src/utils/customValidator";
import {
  ScenarioFormProps,
  JSONValue
} from "@/src/types/components/ScenarioForm";
import DescriptionFieldTemplate from "./FieldTemplates/DescriptionField";
import { transformErrors } from "@/src/utils/transformErrors";
import {
  syncMeasureTemplateIds,
  updateIndicatorsBasedOnMaatregel,
  isMaatregelenForm,
} from "@/src/utils/maatregelenIndicators";
import { RJSFValidationError, RJSFSchema } from "@rjsf/utils";
import { formValues } from "@/src/types/api";
import { IChangeEvent } from "@rjsf/core";
import { Form } from "@rjsf/antd";
import { CheckCircleTwoTone, ExclamationCircleTwoTone } from "@ant-design/icons";
import { usePDFContext } from "@/src/contexts/PDFContext";

export default function ScenarioForm({
  formJson,
  formValues,
  uiSchema,
  handleSubmit,
  buttonText,
}: ScenarioFormProps) {
  const [formData, setFormData] = useState<JSONValue>(formValues || { status: "" });
  const [previousFormData, setPreviousFormData] = useState<JSONValue | null>(null);
  const [errors, setErrors] = useState<RJSFValidationError[]>([]);
  const pathname = usePathname();
  const { isPDF, currentPage } = usePDFContext();

  // When the page is opened in the evaluation, the 'Aanvraag' page should be read-only.
  // This is determined by checking if the pathname includes 'aanvraag'.
  // When the same form is used on the create page, it should not be read-only.
  // This check will later also include a check whether an evaluation is finished or not.
  // In PDF context, check the currentPage; in normal context, use pathname
  let isReadOnly = false;
  const completedForm = formData && typeof formData === 'object' && !Array.isArray(formData) && 'status' in formData && formData.status === 'afgerond';
  if (isPDF) {
    // In PDF context, check currentPage for 'aanvraag'
    isReadOnly = currentPage?.toLowerCase().includes("aanvraag") ?? false;
  } else {
    // In normal web context, only fall back to false if pathname is truly unavailable
    
    const showsAanvraagForm: boolean = pathname ? pathname.includes("aanvraag") : false;
    if(showsAanvraagForm || completedForm) {
      isReadOnly = true;
    }
  }

  // Helper function to check recursively for required fields in the schema
  const checkForRequiredFields = (schema: RJSFSchema): boolean => {
    // Check if this level has required fields
    if (schema.required && schema.required.length > 0) {
      return true;
    }

    // Check for required fields in properties
    if (schema.properties) {
      for (const key in schema.properties) {
        const property = schema.properties[key] as RJSFSchema;
        // If property is an object, recursively check it
        if (property.type === "object" || (Array.isArray(property.type) && property.type.includes("object"))) {
          if (checkForRequiredFields(property)) {
            return true;
          }
        }
      }
    }

    return false;
  };

  async function onSubmit(data: IChangeEvent<formValues>) {
    //reset state
    setErrors([]);

    if (data.formData) {
      const submittedFormData = isMaatregelenForm(formJson)
        ? syncMeasureTemplateIds(data.formData as JSONValue, formJson)
        : data.formData;

      // Check if formData is an empty object but there are required fields in the schema
      const isEmptyObject = Object.keys(submittedFormData).length === 0;
      const hasRequiredFields = checkForRequiredFields(formJson);

      if (isEmptyObject && hasRequiredFields) {
        // Create custom validation error for empty form with required fields
        const requiredError: RJSFValidationError = {
          name: "required",
          message: "Verplichte velden zijn niet ingevuld",
          stack: "Verplichte velden zijn niet ingevuld",
          property: "."
        };
        setErrors([requiredError]);
        return;
      }
      handleSubmit(submittedFormData as formValues);
      setFormData(submittedFormData);
    }
  }

  function handleErrors(errors: RJSFValidationError[]) {
    setErrors(errors);
    const updatedErrors = transformErrors(errors);
    return updatedErrors;
  }

  const log = (type: string) => console.log.bind(console, type);

  return (
    <div className="w-full ">
    {completedForm &&
      <div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-800 rounded">
        Deze evaluatie is afgerond en kan niet meer worden bewerkt.
      </div>
    }
      <Form
        schema={formJson}
        formData={formData}
        validator={validator}
        showErrorList={false}
        transformErrors={handleErrors}
        onSubmit={onSubmit}
        onChange={(e) => {
          // Only process indicator logic for maatregelen forms
          if (isMaatregelenForm(formJson)) {
            const updatedFormData = updateIndicatorsBasedOnMaatregel(e.formData, previousFormData, formJson);
            setPreviousFormData(formData); // Store current as previous for next change
            setFormData(updatedFormData);
          } else {
            setPreviousFormData(formData);
            setFormData(e.formData);
          }
        }}
        readonly={isReadOnly}
        onError={log("errors")}
        formContext={{
          descriptionLocation: "tooltip",
          formData,
        }}
        templates={{ DescriptionFieldTemplate }}
        {...(uiSchema && { uiSchema })}
        noHtml5Validate
      >
        {errors && (
          <div className=" text-red-500 mx-4 mb-4 text-sm">
            {errors.length > 0 && (
              <div>
                Het lijkt erop dat niet alle gegevens correct zijn ingevuld.

              </div>
            )}
          </div>
        )}

        {/* only in form Prestaties show this text  */}
        {formJson.title.toLowerCase().indexOf('prestaties') != -1 &&
          <div className="flex flex-row gap-4 py-2 px-6">
            <span><CheckCircleTwoTone twoToneColor="#52c41a" /> <span className="font-medium">Gelijk of beter dan het HNN-Niveau</span></span>
            <span><ExclamationCircleTwoTone twoToneColor="#d78d17ff" /> <span className="font-medium">Vraagt aandacht</span></span>
          </div>
        }
        {!isReadOnly && (
          <button type="submit" className={`submit-button ml-auto button button-primary flex flex-row`}>
            {buttonText ? buttonText : "Volgende"}
          </button>
        )}
      </Form>
    </div>
  );
}
